// /admin/v1/cardapi/catalog — 供应商 (card-api) 商品目录只读镜像 (cardapi-catalog-sync).
// Mirrors backend cmd/admin handlers:
//   POST /admin/v1/cardapi/catalog/sync  — 拉直充 + 卡密两池入库 + 软下架失效行
//   GET  /admin/v1/cardapi/catalog       — 分页列 supplier_products
//
// ⚠️ sync 走供应商 disclist, 限流 10s/次 — 仅手动触发 (按钮), loading 期间禁用防连点.
//    面值 / 结算价为 NUMERIC 字符串 (后端保精度), 前端直接展示, 切勿做 float 运算.

import { list, post } from './client';
import type { ListResult } from './client';

/** supplier_products 一行 (card-api 直充 / 卡密商品的归一镜像). */
export interface SupplierProduct {
  id: number;
  /** 供应商来源, 目前恒为 'card-api'. */
  source: string;
  /** 直充 (topup) / 卡密 (card). */
  category: 'topup' | 'card';
  /** 供应商产品编码 (直充 CardCode / 卡密 HCamiCode·YCamiCode). */
  product_code: string;
  name: string;
  /** 面值 (NUMERIC 字符串, 直接展示). */
  face_value: string;
  /** 结算价 (NUMERIC 字符串): 直充 = 折后价; 卡密 = 卡种价. 无价时 null. */
  settle_price: string | null;
  /** 折扣 (文本, 供应商原样), 可空. */
  discount: string | null;
  /** 卡密卡种: H = 官方卡, Y = 自生成卡; 直充为空串. */
  card_kind: 'H' | 'Y' | '';
  /** false = 本轮未刷新被软下架. */
  enabled: boolean;
  synced_at: string;
  created_at: string;
  updated_at: string;
}

/** POST /catalog/sync 的统计返回. */
export interface SyncCatalogResult {
  /** 直充池拉到的商品数. */
  topup_total: number;
  /** 卡密池拉到的商品数 (拆 H/Y 行前). */
  card_total: number;
  /** 本轮 upsert (新增 + 更新) 的行数. */
  upserted: number;
  /** 本轮未刷新被软下架的行数. */
  disabled: number;
}

/**
 * 拉取供应商两池入库. ⚠️ 供应商 disclist 限流 10s/次 — 按钮 loading 期间必须禁用防连点.
 * 失败 (IP 未加白 1103 / 限流 1006) 后端返回 502 带可读 message (含受限 IP), 直接 toast.
 */
export function syncCatalog(): Promise<SyncCatalogResult> {
  return post<SyncCatalogResult>('/admin/v1/cardapi/catalog/sync');
}

export interface ListCatalogQuery {
  category?: 'topup' | 'card';
  enabled?: boolean;
  page?: number;
  size?: number;
}

export function listCatalog(q: ListCatalogQuery): Promise<ListResult<SupplierProduct>> {
  return list<SupplierProduct>('/admin/v1/cardapi/catalog', q);
}

/**
 * 「配成 SKU」跨页预填载荷 — 供应商目录页 navigate('/skus', { state: { prefillSKU } }),
 * 商业 SKU 页读取后打开新建 Modal 并预填履约参数. provider: 直充 → card-api-topup, 卡密 → card-api.
 */
export interface CatalogSKUPrefill {
  product_code: string;
  category: 'topup' | 'card';
  face_value: string;
  settle_price: string | null;
  provider: 'card-api' | 'card-api-topup';
}
